/**
 * Booked-time-slot resolution for end-user "booked events" lists.
 *
 * Deliberately DEPENDENCY-FREE. `utils/sort.js` imports this, and 8 of sort.js's 11
 * consumers (DashboardContext among them) do not otherwise pull in `utils/timeSlots.js`.
 * Putting these helpers there would drag date-fns plus its en-US and he locale bundles
 * into every one of those chunks. Keep this file import-free; anything needing date-fns
 * formatting belongs in timeSlots.js.
 */

/**
 * The time slot a booked-event card should DISPLAY, or null when the card must fall back
 * to the event's `start_date`. Returns the record holding the slot fields, ready to hand
 * to `formatAppointmentDateTime`.
 *
 * The gate is deliberately a CONJUNCTION — the event must still be configured as a
 * time-slot event AND the booking must actually carry a slot. Each half alone is wrong,
 * and both halves occur in real data:
 *
 *  - config enabled, booking has NO slot id  → fall back. (Legacy/imported orders on an
 *    event that was later switched to time slots. Taking the slot branch here would
 *    render an empty date.)
 *  - booking HAS a slot id, config disabled  → fall back. (The event was a slot event
 *    when booked and no longer is; the seller now runs it as a plain dated event, so the
 *    card follows the event's current shape.) NOTE: the ticket page (TicketView) shows the
 *    slot for these, so the two surfaces intentionally differ for this case.
 *
 * The slot lives on the eventOrder; `userEvent.timeSlotId` is the per-attendee fallback,
 * mirroring how the card resolves its "booked on" timestamp.
 *
 * @param {Object} userEvent - A userEvent record with nested `event` and `eventOrder`
 * @returns {Object|null} Record carrying timeSlotId/timeSlotDate/timeSlotTime/timeSlotDuration, or null
 */
export const getDisplayedBookedSlot = (userEvent) => {
  if (!userEvent?.event?.timeSlotConfig?.enabled) return null;
  const { eventOrder } = userEvent;
  if (eventOrder?.timeSlotId) return eventOrder;
  if (userEvent.timeSlotId) return userEvent;
  return null;
};

/**
 * UTC calendar-day timestamp for a booked slot, or null when unreadable.
 * Prefers the explicit `timeSlotDate`, falling back to the date half of the slot id
 * ("2026-09-25_1100"), the same precedence formatAppointmentDateTime uses.
 * @pure
 */
export const bookedSlotDayMs = (slot) => {
  const raw = slot?.timeSlotDate || String(slot?.timeSlotId ?? '').split('_')[0];
  const m = String(raw ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // Date.UTC rolls an impossible day over ("2026-02-30" → 2 March); treat it as unreadable.
  const d = new Date(ms);
  if (d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return null;
  return ms;
};

/**
 * Minutes since midnight for a booked slot's start time, or null when unreadable.
 * Accepts "1100" and "11:00" (both shapes occur), falling back to the time half of the
 * slot id. Mirrors formatAppointmentDateTime's parsing so the order matches the display.
 * @pure
 */
export const bookedSlotMinutes = (slot) => {
  const raw = slot?.timeSlotTime || String(slot?.timeSlotId ?? '').split('_')[1];
  const clean = String(raw ?? '').replace(':', '');
  if (!/^\d{3,4}$/.test(clean)) return null;
  const padded = clean.padStart(4, '0');
  const hours = Number(padded.slice(0, 2));
  const minutes = Number(padded.slice(2, 4));
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};
